import * as React from 'react';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { LucideIcon } from './LucideIcon';
import type { Pdf as Interface, FileMetadata } from '../../design/components';
import { isFileMetadata } from '../shared/image';
import { isValidPdfSource, downloadPdf, type PdfDocProxy } from '../shared/pdf';

export const Pdf: React.FC<Interface> = ({
  name,
  description,
  state,
  placeholder = 'No PDF selected',
  hidden,
  inline,
  readonly: _readonly,
  disabled,
  required,
  error,
}) => {
  const [value, setValue] = state || ['' as unknown as FileMetadata, () => {}];
  const valueRef = React.useRef(value);
  valueRef.current = value;
  const [numPages, setNumPages] = React.useState<number | null>(null);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [expanded, setExpanded] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = React.useState<number | undefined>(undefined);
  const [dragOver, setDragOver] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const isValid = isValidPdfSource(value);

  const displayUrl = React.useMemo(() => isValid ? (value as FileMetadata).url : '', [value, isValid]);

  React.useEffect(() => {
    return () => { if (displayUrl.startsWith('blob:')) URL.revokeObjectURL(displayUrl); };
  }, [displayUrl]);

  const handleDocumentLoadSuccess = (pdf: PdfDocProxy) => {
    setNumPages(pdf.numPages);
    const current = valueRef.current;
    if (isFileMetadata(current) && current.file) {
      (async () => {
        const text: string[] = [];
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const content = await page.getTextContent();
          text.push(content.items.map(item => ('str' in item ? item.str : '')).join(' '));
        }
        const searchable = text.some(t => t.length > 0);
        setValue({ ...valueRef.current as FileMetadata, pages: pdf.numPages, searchable, text });
      })().catch(() => {});
    }
  };

  const handleDownload = () => {
    if (isFileMetadata(value)) downloadPdf(value.url, value.name);
  };

  const processFile = (file: File) => {
    if (file.type === 'application/pdf') {
      setValue({
        url: URL.createObjectURL(file),
        name: file.name, type: file.type, size: file.size,
        modified: new Date(file.lastModified).toISOString(),
        pages: 0, file,
      });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  };

  React.useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth - 2);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [isValid]);

  const controlsHeight = 40;
  const previewHeight = 300;
  const containerHeight = previewHeight + controlsHeight;

  // Inline mode: show small icon/indicator
  if (inline) {
    return value ? (
      <LucideIcon name="FileText" className={cn(hidden && 'hidden')} size="small" />
    ) : null;
  }

  const canUpload = !_readonly && !disabled;

  return (
    <div className={cn('flex flex-col gap-2', hidden && 'hidden')} data-disabled={disabled}>
      {/* Label */}
      {name && (
        <Label htmlFor={name} className={cn(error && 'text-destructive')}>
          {name}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
      )}

      {/* PDF Preview */}
      {isValid && (
        <div
          ref={containerRef}
          className={cn(
            'w-full border rounded-md bg-muted/30',
            expanded ? 'overflow-auto' : 'overflow-hidden',
            disabled && 'opacity-50'
          )}
          style={expanded ? { maxHeight: '80vh' } : { height: containerHeight }}
        >
          {/* Controls */}
          <div className="sticky top-0 z-10 flex items-center justify-center gap-1 py-2 border-b bg-background/80 backdrop-blur-sm">
            {numPages && numPages > 1 && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage <= 1}
                >
                  <LucideIcon name="ChevronLeft" size="small" />
                </Button>
                <span className="text-xs text-muted-foreground">
                  {currentPage} / {numPages}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => setCurrentPage(p => Math.min(numPages, p + 1))}
                  disabled={currentPage >= numPages}
                >
                  <LucideIcon name="ChevronRight" size="small" />
                </Button>
              </>
            )}

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => setExpanded(!expanded)}
                >
                  <LucideIcon name={expanded ? 'Minimize' : 'Maximize'} size="small" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{expanded ? 'Shrink' : 'Expand'}</TooltipContent>
            </Tooltip>

            {isFileMetadata(value) && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    asChild
                  >
                    <a href={value.url} target="_blank" rel="noopener noreferrer">
                      <LucideIcon name="ExternalLink" size="small" />
                    </a>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Open in new tab</TooltipContent>
              </Tooltip>
            )}

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={handleDownload}
                >
                  <LucideIcon name="Download" size="small" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Download</TooltipContent>
            </Tooltip>

            {canUpload && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => setValue('' as unknown as FileMetadata)}
                  >
                    <LucideIcon name="Trash2" size="small" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Remove</TooltipContent>
              </Tooltip>
            )}
          </div>

          <Document
            file={displayUrl}
            onLoadSuccess={handleDocumentLoadSuccess}
            className="flex justify-center"
            loading={<div className="p-4 text-sm text-muted-foreground">Loading...</div>}
            error={<div className="p-4 text-sm text-destructive">Failed to load PDF</div>}
            noData={<div className="p-4 text-sm text-muted-foreground">No PDF</div>}
          >
            <Page
              pageNumber={currentPage}
              width={expanded ? containerWidth : undefined}
              height={expanded ? undefined : previewHeight}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              loading={<div className="p-4 text-sm text-muted-foreground">Loading page...</div>}
              error={<div className="p-4 text-sm text-destructive">Failed to load page</div>}
            />
          </Document>
        </div>
      )}

      {/* Empty state */}
      {!isValid && canUpload && (
        <div
          className={cn(
            'flex flex-col items-center justify-center h-24 rounded-md border border-dashed bg-muted/50 text-muted-foreground cursor-pointer transition-colors',
            dragOver && 'border-primary bg-primary/5',
            error && 'border-destructive'
          )}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
        >
          <LucideIcon name="Upload" size="large" className="mb-1" />
          <span className="text-sm">Click or drag to upload PDF</span>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            className="hidden"
            onChange={handleFileChange}
          />
        </div>
      )}

      {/* Static empty state for readonly/disabled */}
      {!isValid && !canUpload && (
        <div
          className={cn(
            'flex items-center justify-center h-24 rounded-md border bg-muted/50 text-muted-foreground',
            error && 'border-destructive'
          )}
        >
          <LucideIcon name="FileText" size="large" className="mr-2" />
          <span>{placeholder}</span>
        </div>
      )}

      {(error || description) && (
        <p className={cn('text-sm', error ? 'text-destructive' : 'text-muted-foreground')}>
          {error || description}
        </p>
      )}
    </div>
  );
};
